import React, { useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import { useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RouteIcon from "@mui/icons-material/Route";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  useGetMasteryWorkflowQuery,
  useCreateMasteryWorkflowMutation,
  useDeleteMasteryWorkflowMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import DeletePopup from "../../../Components/Delete/DeletePopup";

interface IPhase {
  phaseName: string;
  transitionsTo: string;
  minPercentCorrect: number;
  acrossSessions: number;
  minTrialsPerSession: number;
}

interface IWorkflow {
  id: string;
  workflowName: string;
  objective: string;
  isDefault: boolean;
  phases: IPhase[];
}

// The phaseName values here correspond to a program_target.status the workflow
// transitions the target to when the criteria are met. Keeping the list open
// (freeform text) is intentional — the backend does not enforce an enum.
const PHASE_NAME_OPTIONS = ["baseline", "acquisition", "mastered", "maintenance", "hold"];
const TRANSITION_OPTIONS = ["acquisition", "mastered", "maintenance", "closed"];

const emptyPhase: IPhase = {
  phaseName: "acquisition",
  transitionsTo: "mastered",
  minPercentCorrect: 80,
  acrossSessions: 3,
  minTrialsPerSession: 10,
};

// Standard starter workflow — probe → acquisition → mastered → maintenance.
// Presented as a template; nothing writes until the user clicks Save.
const STARTER_WORKFLOW: { workflowName: string; objective: string; phases: IPhase[] } = {
  workflowName: "Standard Workflow",
  objective: "Move targets from baseline through acquisition to mastered, then maintenance",
  phases: [
    { phaseName: "baseline", transitionsTo: "acquisition", minPercentCorrect: 0, acrossSessions: 1, minTrialsPerSession: 10 },
    { phaseName: "acquisition", transitionsTo: "mastered", minPercentCorrect: 80, acrossSessions: 3, minTrialsPerSession: 10 },
    { phaseName: "mastered", transitionsTo: "maintenance", minPercentCorrect: 90, acrossSessions: 2, minTrialsPerSession: 5 },
    { phaseName: "maintenance", transitionsTo: "closed", minPercentCorrect: 90, acrossSessions: 1, minTrialsPerSession: 3 },
  ],
};

// One rounded pill in the phase chain. Colour-coded to match the program
// target status colours used elsewhere in the app.
const PhaseChip: React.FC<{ phase: IPhase; index: number }> = ({ phase, index }) => {
  const theme = useTheme();
  const hueMap: Record<string, string> = {
    baseline: theme.palette.info.main,
    acquisition: theme.palette.primary.main,
    mastered: theme.palette.success.main,
    maintenance: theme.palette.success.dark,
    hold: theme.palette.warning.main,
    closed: theme.palette.grey[500],
  };
  const color = hueMap[phase.phaseName?.toLowerCase()] || theme.palette.primary.main;
  return (
    <Box
      sx={{
        borderRadius: 999,
        px: 1.5,
        py: 0.75,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        backgroundColor: alpha(color, 0.12),
        border: `1px solid ${alpha(color, 0.4)}`,
        color,
      }}
    >
      <Chip
        label={index + 1}
        size="small"
        sx={{ width: 20, height: 18, fontSize: 10, fontWeight: 700, bgcolor: color, color: theme.palette.getContrastText(color) }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "capitalize", lineHeight: 1.2 }}>
          {phase.phaseName || "(unnamed)"}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", lineHeight: 1.2 }}>
          ≥ {Number(phase.minPercentCorrect ?? 0).toFixed(0)}% × {phase.acrossSessions ?? 1}
        </Typography>
      </Box>
    </Box>
  );
};

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<IWorkflow | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<IWorkflow | null>(null);

  const { data, isLoading, isError, refetch } = useGetMasteryWorkflowQuery({});
  const [createWorkflow, { isLoading: creating }] = useCreateMasteryWorkflowMutation();
  const [deleteWorkflow] = useDeleteMasteryWorkflowMutation();

  const workflows: IWorkflow[] = Array.isArray(data?.data) ? (data!.data as IWorkflow[]) : [];

  const formik = useFormik({
    initialValues: {
      workflowName: "",
      objective: "",
      isDefault: false,
      phases: [{ ...emptyPhase }] as IPhase[],
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        ...values,
        id: editingWorkflow?.id,
        phases: values.phases.filter((p) => (p.phaseName || "").trim().length > 0),
      };
      if (payload.phases.length === 0) {
        showErrorMessage("Add at least one phase.");
        return;
      }
      try {
        const response = await createWorkflow(payload).unwrap();
        if (response.status) {
          showMessage(response.message || "Workflow saved");
          setFormDialogOpen(false);
          resetForm();
          setEditingWorkflow(null);
          refetch();
        } else {
          showErrorMessage(response.message || "Failed to save workflow");
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleOpenCreate = () => {
    setEditingWorkflow(null);
    formik.resetForm();
    setFormDialogOpen(true);
  };

  const handleOpenEdit = (workflow: IWorkflow) => {
    setEditingWorkflow(workflow);
    formik.setValues({
      workflowName: workflow.workflowName,
      objective: workflow.objective || "",
      isDefault: workflow.isDefault,
      phases: workflow.phases?.length
        ? workflow.phases.map((p) => ({
            phaseName: p.phaseName,
            transitionsTo: p.transitionsTo,
            minPercentCorrect: Number(p.minPercentCorrect ?? 80),
            acrossSessions: Number(p.acrossSessions ?? 1),
            minTrialsPerSession: Number(p.minTrialsPerSession ?? 1),
          }))
        : [{ ...emptyPhase }],
    });
    setFormDialogOpen(true);
  };

  const handleApplyStarter = () => {
    setEditingWorkflow(null);
    formik.setValues({
      workflowName: STARTER_WORKFLOW.workflowName,
      objective: STARTER_WORKFLOW.objective,
      isDefault: workflows.length === 0,
      phases: STARTER_WORKFLOW.phases.map((p) => ({ ...p })),
    });
    setFormDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteWorkflow(id).unwrap();
      if (response.status) {
        showMessage(response.message || "Workflow deleted");
        setDeleteDialogOpen(false);
        setWorkflowToDelete(null);
        refetch();
      } else {
        showErrorMessage(response.message || "Failed to delete workflow");
      }
    } catch {
      showErrorMessage("Failed to delete workflow");
    }
  };

  const addPhase = () => {
    formik.setFieldValue("phases", [...formik.values.phases, { ...emptyPhase }]);
  };
  const removePhase = (index: number) => {
    const updated = formik.values.phases.filter((_, i) => i !== index);
    formik.setFieldValue("phases", updated.length ? updated : [{ ...emptyPhase }]);
  };
  const movePhase = (from: number, to: number) => {
    if (to < 0 || to >= formik.values.phases.length) return;
    const next = [...formik.values.phases];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    formik.setFieldValue("phases", next);
  };

  return (
    <Box>
      {/* Explainer strip */}
      <Card
        className={classes.card}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2,
          display: "flex",
          gap: 2,
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Workflows
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            A mastery workflow is the chain of phases a target passes through and the criteria for
            advancing between them — for example, ≥ 80% correct across 3 sessions moves a target from
            acquisition to mastered. Each phase name matches the status the target moves to.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={handleApplyStarter}>
            Use standard workflow
          </Button>
          <Button
            variant="contained"
            className={classes.addNewButton}
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            New Workflow
          </Button>
        </Stack>
      </Card>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="30vh">
          <StepUpSpinner size="medium" />
        </Box>
      ) : isError ? (
        <Card className={classes.card} sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error">Error fetching workflows.</Typography>
        </Card>
      ) : workflows.length === 0 ? (
        <Card
          className={classes.card}
          sx={{
            p: { xs: 3, md: 5 },
            textAlign: "center",
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <RouteIcon sx={{ fontSize: 44, color: "text.disabled", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            No mastery workflows yet
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 520, mx: "auto", mb: 2 }}>
            Start from the standard workflow (probe → acquisition → mastered → maintenance) and adjust the
            thresholds to how your clinic works, or build one from scratch. Nothing saves until you click
            Save inside the editor.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="center">
            <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={handleApplyStarter}>
              Use standard workflow
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Build from scratch
            </Button>
          </Stack>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {workflows.map((workflow) => (
            <Grid item xs={12} md={6} lg={6} key={workflow.id}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  borderTop: `3px solid ${workflow.isDefault ? theme.palette.success.main : theme.palette.primary.main}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {workflow.workflowName}
                    </Typography>
                    {workflow.objective && (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {workflow.objective}
                      </Typography>
                    )}
                  </Box>
                  {workflow.isDefault ? (
                    <Chip icon={<StarIcon />} label="Default" size="small" color="success" variant="outlined" />
                  ) : (
                    <Chip icon={<StarBorderIcon />} label="—" size="small" variant="outlined" sx={{ visibility: "hidden" }} />
                  )}
                </Box>

                <Divider sx={{ my: 0.5 }} />

                {/* Phase chain — arrows between chips make the sequence the point. */}
                <Box sx={{ flexGrow: 1 }}>
                  {workflow.phases && workflow.phases.length > 0 ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      alignItems="center"
                      sx={{ rowGap: 1 }}
                    >
                      {workflow.phases.map((phase, idx) => (
                        <React.Fragment key={idx}>
                          <PhaseChip phase={phase} index={idx} />
                          {idx < workflow.phases.length - 1 && (
                            <ArrowForwardIcon fontSize="small" sx={{ color: "text.disabled" }} />
                          )}
                        </React.Fragment>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      No phases configured.
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                  <IconButton size="small" onClick={() => handleOpenEdit(workflow)}>
                    <EditSharpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setWorkflowToDelete(workflow);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <DeleteOutlineSharpIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingWorkflow ? "Edit Workflow" : "New Workflow"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Workflow name <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                placeholder="e.g. Standard Workflow"
                size="small"
                fullWidth
                {...formik.getFieldProps("workflowName")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                sx={{ mt: { md: 3 } }}
                control={
                  <Checkbox
                    checked={formik.values.isDefault}
                    onChange={(e) => formik.setFieldValue("isDefault", e.target.checked)}
                  />
                }
                label="Use as default"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Objective
              </Typography>
              <TextField
                placeholder="What outcome this workflow is optimising for"
                size="small"
                fullWidth
                {...formik.getFieldProps("objective")}
              />
            </Grid>

            {/* Live preview of the phase chain */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                  Preview
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ rowGap: 1 }}>
                  {formik.values.phases.map((phase, idx) => (
                    <React.Fragment key={idx}>
                      <PhaseChip phase={phase} index={idx} />
                      {idx < formik.values.phases.length - 1 && (
                        <ArrowForwardIcon fontSize="small" sx={{ color: "text.disabled" }} />
                      )}
                    </React.Fragment>
                  ))}
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Phases
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  In order — first is where a new target starts
                </Typography>
              </Box>

              <Stack spacing={1.25}>
                {formik.values.phases.map((phase, index) => (
                  <Card key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs="auto">
                        <Chip label={index + 1} size="small" sx={{ fontWeight: 700 }} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>Phase</Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={phase.phaseName}
                            onChange={(e) => formik.setFieldValue(`phases.${index}.phaseName`, e.target.value)}
                          >
                            {PHASE_NAME_OPTIONS.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>On success → status</Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={phase.transitionsTo}
                            onChange={(e) => formik.setFieldValue(`phases.${index}.transitionsTo`, e.target.value)}
                          >
                            {TRANSITION_OPTIONS.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>Min %</Typography>
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          value={phase.minPercentCorrect}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `phases.${index}.minPercentCorrect`,
                              Math.min(100, Math.max(0, Number(e.target.value)))
                            )
                          }
                          InputProps={{ inputProps: { min: 0, max: 100 } }}
                        />
                      </Grid>
                      <Grid item xs={4} sm={1.5}>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>Sessions</Typography>
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          value={phase.acrossSessions}
                          onChange={(e) =>
                            formik.setFieldValue(`phases.${index}.acrossSessions`, Math.max(1, Number(e.target.value)))
                          }
                          InputProps={{ inputProps: { min: 1 } }}
                        />
                      </Grid>
                      <Grid item xs={4} sm={1.5}>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>Min trials</Typography>
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          value={phase.minTrialsPerSession}
                          onChange={(e) =>
                            formik.setFieldValue(`phases.${index}.minTrialsPerSession`, Math.max(1, Number(e.target.value)))
                          }
                          InputProps={{ inputProps: { min: 1 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm="auto">
                        <Box sx={{ display: "flex", gap: 0.25 }}>
                          <IconButton size="small" onClick={() => movePhase(index, index - 1)} disabled={index === 0}>
                            <ArrowUpwardIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => movePhase(index, index + 1)}
                            disabled={index === formik.values.phases.length - 1}
                          >
                            <ArrowDownwardIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => removePhase(index)}
                            disabled={formik.values.phases.length === 1}
                          >
                            <DeleteOutlineSharpIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </Stack>

              <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                <Button size="small" startIcon={<AddIcon />} onClick={addPhase}>
                  Add phase
                </Button>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() =>
                    formik.setFieldValue(
                      "phases",
                      STARTER_WORKFLOW.phases.map((p) => ({ ...p }))
                    )
                  }
                >
                  Replace with standard workflow
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFormDialogOpen(false);
              formik.resetForm();
              setEditingWorkflow(null);
            }}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => formik.handleSubmit()}
            disabled={!formik.values.workflowName.trim() || creating}
            startIcon={creating ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {workflowToDelete && (
        <DeletePopup
          open={deleteDialogOpen}
          handleCloseDelete={() => {
            setDeleteDialogOpen(false);
            setWorkflowToDelete(null);
          }}
          onConfirm={async () => {
            await handleDelete(workflowToDelete.id);
          }}
          title="Delete Workflow"
          content={`Are you sure you want to delete "${workflowToDelete.workflowName}"?`}
        />
      )}
    </Box>
  );
};

export default Index;
